import { useState } from 'react';
import { ErreurApi, api } from '../lib/apiClient';
import { formaterDateHeure } from '../lib/format';
import { Badge } from './Elements';
import type { EtatCompteGmail } from '../types';

/**
 * LE COMPTE GMAIL DE L'UTILISATEUR (0.73.0) : celui dans lequel
 * l'application pose ses brouillons — l'attestation de paiement avec sa
 * pièce jointe. L'accès se demande À LA CONNEXION GOOGLE, à qui détient
 * la permission « Envoi d'email » : ce bloc ne fait que montrer l'état
 * et, dans Mes informations, retirer l'accès. Version complète ou
 * compacte, pour les deux modales.
 */
export function CompteGmail({
  etat,
  recharger,
  permission,
  compact = false,
}: {
  etat: EtatCompteGmail | null;
  recharger: () => void;
  /** L'utilisateur détient « Envoi d'email » sur l'école active. */
  permission: boolean;
  compact?: boolean;
}) {
  const [message, setMessage] = useState<{ ton: 'ok' | 'erreur'; texte: string } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const connexion = etat?.connexion ?? null;

  const deconnecter = async () => {
    if (!window.confirm('Retirer l’accès à votre boîte Gmail ? Il sera redemandé à votre prochaine connexion avec Google.')) return;
    setEnCours(true);
    try {
      await api.delete('/api/auth/google/mail');
      setMessage({ ton: 'ok', texte: 'Accès retiré.' });
      recharger();
    } catch (cause) {
      setMessage({ ton: 'erreur', texte: cause instanceof ErreurApi ? cause.message : 'Retrait impossible.' });
    } finally {
      setEnCours(false);
    }
  };

  const nonConfigure = etat !== null && !etat.configure;
  const explication = !permission
    ? 'Votre rôle ne comprend pas l’envoi d’email : les attestations passent par le téléchargement.'
    : nonConfigure
      ? 'Le client OAuth Google n’est pas configuré sur ce serveur : rien ne peut se connecter.'
      : etat?.aReconnecter
        ? etat.motifReconnexion === 'jeton'
          ? 'Le jeton ne se déchiffre pas avec la clé de ce serveur : il sera redemandé à votre prochaine connexion avec Google.'
          : 'L’accès accordé ne permet pas de composer des messages : il sera redemandé à votre prochaine connexion avec Google.'
        : connexion
          ? null
          : 'L’accès vous sera demandé à votre prochaine connexion avec « Se connecter avec Google ».';

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {!compact && (
        <>
          <h3 className="etiquette">Messagerie Gmail</h3>
          <p className="text-sm text-encre-600">
            Votre boîte Gmail, dans laquelle l’application pose les messages qu’elle prépare — l’attestation de
            paiement avec son PDF en pièce jointe. Rien ne part sans vous : vous relisez et envoyez depuis Gmail.
            L’application ne lit jamais votre boîte.
          </p>
        </>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {connexion ? (
          <>
            <Badge ton={etat?.aReconnecter ? 'alerte' : 'succes'}>{etat?.aReconnecter ? 'à renouveler' : 'accès accordé'}</Badge>
            <span className="font-medium">{connexion.compte}</span>
            {!compact && (
              <span className="text-xs text-encre-500">
                depuis le {formaterDateHeure(connexion.connecte_le)}
                {connexion.derniere_utilisation
                  ? ` · dernière utilisation le ${formaterDateHeure(connexion.derniere_utilisation)}`
                  : ' · jamais utilisé'}
              </span>
            )}
          </>
        ) : (
          <>
            <Badge ton="neutre">pas d’accès</Badge>
            {compact && <span className="text-xs text-encre-500">le PDF sera téléchargé, à joindre vous-même.</span>}
          </>
        )}
      </div>

      {explication && <p className={`text-xs ${permission ? 'text-encre-500' : 'text-encre-500'}`}>{explication}</p>}

      {connexion?.derniere_erreur && !compact && (
        <p className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          Dernière erreur : {connexion.derniere_erreur}
        </p>
      )}

      {message && (
        <p
          className={`rounded-md border p-3 text-sm ${
            message.ton === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-red-200 bg-red-50 text-red-800'
          }`}
          role="status"
        >
          {message.texte}
        </p>
      )}

      {connexion && !compact && (
        <div>
          <button type="button" className="bouton-secondaire" disabled={enCours} onClick={() => void deconnecter()}>
            Retirer l’accès
          </button>
        </div>
      )}
    </div>
  );
}
